import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import GridPolaris from "../../assets/images/GridPolaris.svg";
import LewisLogo from "../../assets/images/lewisCounty.png";
import bg_image from "../../assets/images/role-selection-screen-bg.svg";
import { updateUserRole } from "../../services/loginInfo";
import { MuiCustomModal } from "../../shared/Modal/MuiCustomModal";
import {
  getRoleDisplayName,
  isGridPolaris,
  selectedTheme,
  tenants,
  userStatus,
} from "../../utils/constant";

export const LoginType = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);
  const [loginType, setLoginType] = useState("");
  const [error, setError] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const role = userInfo.data.role || "";
  const [searchParams, setSearchParams] = useSearchParams();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const back_type = searchParams.get("back");

  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = () => {
    if (loginType === "") setError(true);
    if (loginType !== "") {
      setError(false);
      dispatch(updateUserRole({ role: loginType.toLocaleLowerCase() })).then(
        (res) => {
          if (res?.meta?.requestStatus === "fulfilled") {
            searchParams.delete("back");
            setSearchParams(searchParams);
            setShouldRedirect(true);
          }
        }
      );
    }
  };

  useEffect(() => {
    if (shouldRedirect) {
      if (selectedTheme === "lca") {
        if (loginType === "mentee" && !back_type) {
          navigate("/mentee-question");
          setShouldRedirect(false);
          return;
        }
      }
      else if(selectedTheme === tenants.BPS){
        if(loginType === "mentee"){
          navigate("/mentee-form")
        }
        setShouldRedirect(false);
        return;
      }
       else if (selectedTheme === tenants.TIM) {
        if (
          ["mentee", "volunteer", "mentor", "event_manager"].includes(loginType)
        ) {
          navigate("/questions");
        } else if (loginType === "volunteer_photographer") {
          navigate("/dashboard");
        } else if (loginType === "make_a_request") {
          navigate("/dashboard");
        }
        setShouldRedirect(false);
        return;
      } else if (["mentor", "event_manager"].includes(loginType)) {
        if (selectedTheme === "lca") {
          setIsPopupOpen(true);
        } else {
          navigate("/questions");
        }
        setShouldRedirect(false);
        return;
      } else if (["mentee", "volunteer"].includes(loginType)) {
        if (selectedTheme === "lca") {
          navigate("/mentee-question");
        } else {
          navigate("/individual");
        }
        setShouldRedirect(false);
        return;
      }
    }

    // Original useEffect logic for existing user roles
    if (["mentor", "event_manager"].includes(role)) {
      if (!userInfo.loading && userInfo.status === userStatus.role) {
        if (selectedTheme === "lca") {
          setIsPopupOpen(true);
        } else {
          navigate("/questions");
        }
      }

      if (userInfo?.data?.role && userInfo?.data?.role !== "fresher") {
        if (userInfo.data.is_registered && userInfo?.is_questions_completed) {
          navigate("/dashboard");
        } else {
          if (selectedTheme === "lca") {
            setIsPopupOpen(true);
          } else {
            navigate("/questions");
          }
        }
      }
    } else if (["mentee", "volunteer"].includes(role)) {
      if (selectedTheme === "lca") {
        navigate("/mentee-question");
      } else {
        navigate("/individual");
      }
    }
  }, [userInfo, searchParams, role, shouldRedirect, loginType, back_type]);

  const handleBack = () => {
    navigate(-1);
    localStorage.clear();
  };

  const eventManager = isGridPolaris ? "event_manager" : "mentor";

  const volunteer = isGridPolaris ? "volunteer" : "mentee";

  return (
    <div className="h-full min-h-screen">
      <div className="flex flex-wrap h-full">
        <div className="min-h-screen w-full">
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={userInfo.loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <div
            className="min-h-screen bg-white shadow-lg flex flex-col"
            style={{
              background: `url(${bg_image}) center / cover`,
              backgroundAttachment: isMobile ? "scroll" : "fixed",
            }}
          >
            {/* Back button */}
            <div className="p-4 sm:p-6">
              <div
                className="border border-black rounded-full h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleBack()}
              >
                <KeyboardArrowLeftIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
              </div>
            </div>

            {/* Main content */}
            <div className="flex justify-center items-center px-4 sm:px-6 lg:px-8 pb-8 flex-1">
              <div className="w-full">
                <div className="text-center">
                  {/* Logo section */}
                  <div className="flex justify-center items-center mb-6 sm:mb-8">
                    {selectedTheme === "lca" && (
                      <div className="flex flex-col sm:flex-row gap-3 items-center">
                        <img
                          className="w-12 h-12 sm:w-[50px] sm:h-[50px]"
                          src={LewisLogo}
                          alt="Lewis county"
                        />
                        <div className="text-[#1D5BBF] text-xl sm:text-2xl font-semibold">
                          Lewis County
                        </div>
                      </div>
                    )}
                    {[tenants.MMA, tenants.VMS].includes(selectedTheme) && (
                      <div className="flex items-center justify-center">
                        <img
                          className="w-[180px] h-[70px] sm:w-[250px] sm:h-[100px]"
                          src={GridPolaris}
                          alt="GridPolarisLogo"
                        />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="mb-8 sm:mb-10 text-xl sm:text-2xl lg:text-3xl font-semibold defaultTextColor px-4">
                    Who would you like to continue as?
                  </h3>
                </div>

                {[tenants.TIM, tenants.VMS, tenants.BPS].includes(selectedTheme) && (
                  <form>
                    {/* Role selection */}
                    <div className="pb-6 sm:pb-8">
                      <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-10 px-4 max-w-xl mx-auto justify-center">
                        {/* Mentor option */}
                        <div className="relative">
                          <label
                            className={`flex flex-row p-6 sm:p-8 lg:p-10 w-full justify-center cursor-pointer rounded-lg  ${
                              loginType === eventManager
                                ? `activeTypeBg ${
                                    selectedTheme === "mma"
                                      ? "bg-right-gradient"
                                      : "bg-background-primary-main"
                                  } shadow-lg transform scale-105`
                                : `nonActiveTypeBg bg-transparent border-2 border-gray-300 hover:border-gray-400`
                            }`}
                          >
                            <input
                              type="radio"
                              name="radio"
                              className="hidden"
                              checked={loginType === eventManager}
                              onChange={() => setLoginType(eventManager)}
                            />
                            <span
                              className={`font-semibold text-sm sm:text-base ${
                                loginType === eventManager
                                  ? "text-white"
                                  : "text-gray-700"
                              }`}
                            >
                              {getRoleDisplayName(eventManager)}
                            </span>
                          </label>
                        </div>

                        {/* Mentee option */}
                        <div className="relative">
                          <label
                            className={`flex flex-row p-6 sm:p-8 lg:p-10 w-full justify-center cursor-pointer rounded-lg  ${
                              loginType === volunteer
                                ? `activeTypeBg ${
                                    selectedTheme === "mma"
                                      ? "bg-right-gradient"
                                      : "bg-background-primary-main"
                                  } shadow-lg transform scale-105`
                                : `nonActiveTypeBg bg-transparent border-2 border-gray-300 hover:border-gray-400`
                            }`}
                          >
                            <input
                              type="radio"
                              name="radio"
                              className="hidden"
                              checked={loginType === volunteer}
                              onChange={() => setLoginType(volunteer)}
                            />
                            <span
                              className={`font-semibold text-sm sm:text-base ${
                                loginType === volunteer
                                  ? "text-white"
                                  : "text-gray-700"
                              }`}
                            >
                              {getRoleDisplayName(volunteer)}
                            </span>
                          </label>
                        </div>

                        {isGridPolaris && (
                          <>
                            <div className="r">
                              <label
                                className={`flex flex-row p-6 sm:p-8 lg:p-10 w-full justify-center cursor-pointer rounded-lg  ${
                                  loginType === "mentor"
                                    ? `activeTypeBg ${
                                        selectedTheme === "mma"
                                          ? "bg-right-gradient"
                                          : "bg-background-primary-main"
                                      } shadow-lg transform scale-105`
                                    : `nonActiveTypeBg bg-transparent border-2 border-gray-300 hover:border-gray-400`
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="radio"
                                  className="hidden"
                                  checked={loginType === "mentor"}
                                  onChange={() => setLoginType("mentor")}
                                />
                                <span
                                  className={`font-semibold text-sm sm:text-base ${
                                    loginType === "mentor"
                                      ? "text-white"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {"Mentor"}
                                </span>
                              </label>
                            </div>

                            <div className="r">
                              <label
                                className={`flex flex-row p-6 sm:p-8 lg:p-10 w-full justify-center cursor-pointer rounded-lg  ${
                                  loginType === "mentee"
                                    ? `activeTypeBg ${
                                        selectedTheme === "mma"
                                          ? "bg-right-gradient"
                                          : "bg-background-primary-main"
                                      } shadow-lg transform scale-105`
                                    : `nonActiveTypeBg bg-transparent border-2 border-gray-300 hover:border-gray-400`
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="radio"
                                  className="hidden"
                                  checked={loginType === "mentee"}
                                  onChange={() => setLoginType("mentee")}
                                />
                                <span
                                  className={`font-semibold text-sm sm:text-base ${
                                    loginType === "mentee"
                                      ? "text-white"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {"Mentee"}
                                </span>
                              </label>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Error message */}
                      {error && (
                        <p
                          className="error text-center mt-4 text-sm sm:text-base"
                          role="alert"
                        >
                          Please Select type
                        </p>
                      )}
                    </div>

                    {/* Submit button */}
                    <div className="text-center">
                      <button
                        type="button"
                        className={`inline-block rounded-lg px-6 sm:px-7 py-3 text-sm font-medium text-white w-full sm:w-44  hover:shadow-lg ${
                          selectedTheme === "mma"
                            ? "bg-right-gradient"
                            : "bg-background-primary-main"
                        }`}
                        data-twe-ripple-init
                        data-twe-ripple-color="light"
                        onClick={handleSubmit}
                      >
                        Let's get started
                      </button>
                    </div>
                  </form>
                )}
                {/* {selectedTheme === tenants.TIM && (
                  <form>
                    <div className="pb-6 sm:pb-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 px-4 max-w-4xl mx-auto">
                      
                        <div className="relative">
                          <label
                            className={`flex flex-col p-6 sm:p-8 lg:p-10 w-full justify-center items-center cursor-pointer rounded-lg transition-all duration-200 min-h-[100px] sm:min-h-[120px] lg:min-h-[140px] ${
                              loginType === "mentor"
                                ? `activeTypeBg ${
                                    selectedTheme === "mma"
                                      ? "bg-right-gradient"
                                      : "bg-background-primary-main"
                                  } shadow-lg transform scale-105`
                                : `nonActiveTypeBg bg-transparent border-2 border-gray-300 hover:border-gray-400 hover:shadow-md`
                            }`}
                          >
                            <input
                              type="radio"
                              name="radio"
                              className="hidden"
                              checked={loginType === "mentor"}
                              onChange={() => setLoginType("mentor")}
                            />
                            <span
                              className={`font-semibold text-sm sm:text-base lg:text-lg text-center leading-relaxed ${
                                loginType === "mentor"
                                  ? "text-white"
                                  : "text-gray-700"
                              }`}
                            >
                              {"Mentor"}
                            </span>
                          </label>
                        </div>

                    
                        <div className="relative">
                          <label
                            className={`flex flex-col p-6 sm:p-8 lg:p-10 w-full justify-center items-center cursor-pointer rounded-lg transition-all duration-200 min-h-[100px] sm:min-h-[120px] lg:min-h-[140px] ${
                              loginType === "mentee"
                                ? `activeTypeBg ${
                                    selectedTheme === "mma"
                                      ? "bg-right-gradient"
                                      : "bg-background-primary-main"
                                  } shadow-lg transform scale-105`
                                : `nonActiveTypeBg bg-transparent border-2 border-gray-300 hover:border-gray-400 hover:shadow-md`
                            }`}
                          >
                            <input
                              type="radio"
                              name="radio"
                              className="hidden"
                              checked={loginType === "mentee"}
                              onChange={() => setLoginType("mentee")}
                            />
                            <span
                              className={`font-semibold text-sm sm:text-base lg:text-lg text-center leading-relaxed ${
                                loginType === "mentee"
                                  ? "text-white"
                                  : "text-gray-700"
                              }`}
                            >
                              {"Mentee"}
                            </span>
                          </label>
                        </div>
                      </div>

                    
                      {error && loginType?.length === 0 && (
                        <p
                          className="error text-center mt-4 text-sm sm:text-base text-red-500"
                          role="alert"
                        >
                          Please select type
                        </p>
                      )}
                    </div>

                    <div className="text-center flex justify-center mt-6 sm:mt-8">
                      <button
                        type="button"
                        className="inline-block rounded px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-medium text-white w-full max-w-xs sm:max-w-sm bg-background-primary-main hover:opacity-90 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-background-primary-main"
                        data-twe-ripple-init
                        data-twe-ripple-color="light"
                        onClick={handleSubmit}
                      >
                        Let's get started
                      </button>
                    </div>
                  </form>
                )} */}
              </div>
            </div>

            {/* Modal */}
            <MuiCustomModal
              open={isPopupOpen}
              maxWidth={"sm"}
              actionButtons={[
                {
                  color: "primary",
                  variant: "contained",
                  children: "Next",
                  onClick: () => navigate("/mentor-application-form"),
                },
              ]}
            >
              <h6 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-center text-gray-800 mt-4 sm:mt-6 mb-4">
                {"Welcome!"}
              </h6>
              <p className="text-center text-base sm:text-lg px-4">
                {
                  "Thank you for your interest in our mentoring program. All information collected on this application will be kept confidential within the mentoring program. Click next to continue"
                }
              </p>
            </MuiCustomModal>
          </div>
        </div>
      </div>
    </div>
  );
};
